import React from 'react';
import {
  AlertTriangle,
  CircleX,
  Info,
  Lightbulb,
} from 'lucide-react';
import './StockWarning.css';

const iconProps = { size: 18, strokeWidth: 1.75 };
const headerIconProps = { size: 22, strokeWidth: 1.75 };

/**
 * StockWarning Component
 * Displays warning when cart items exceed available stock
 *
 * @param {Array} issues - Array of stock validation issues
 * @param {Function} onRemoveItems - Callback to remove problematic items
 */
const StockWarning = ({ issues, onRemoveItems }) => {
  if (!issues || issues.length === 0) {
    return null;
  }

  const handleRemoveItem = (productId) => {
    if (onRemoveItems) {
      onRemoveItems(productId);
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'error':
        return 'stock-warning-error';
      case 'warning':
        return 'stock-warning-warning';
      case 'info':
        return 'stock-warning-info';
      default:
        return 'stock-warning-warning';
    }
  };

  const SeverityIcon = ({ severity }) => {
    switch (severity) {
      case 'error':
        return <CircleX {...iconProps} className="issue-icon-svg" aria-hidden />;
      case 'info':
        return <Info {...iconProps} className="issue-icon-svg" aria-hidden />;
      default:
        return <AlertTriangle {...iconProps} className="issue-icon-svg" aria-hidden />;
    }
  };

  return (
    <div className="stock-warning-container">
      <div className="stock-warning-header">
        <span className="stock-warning-icon" aria-hidden>
          <AlertTriangle {...headerIconProps} />
        </span>
        <h3>Stock Availability Issues</h3>
      </div>

      <div className="stock-warning-message">
        Some items in your cart exceed available stock or don't meet minimum order requirements.
        Please review and adjust quantities before proceeding to checkout.
      </div>

      <div className="stock-warning-issues">
        {issues.map((issue, index) => (
          <div
            key={`${issue.productId}-${index}`}
            className={`stock-warning-issue ${getSeverityClass(issue.severity)}`}
          >
            <div className="issue-content">
              <div className="issue-icon">
                <SeverityIcon severity={issue.severity} />
              </div>
              <div className="issue-details">
                <div className="issue-product-name">{issue.productName}</div>
                <div className="issue-message">{issue.message}</div>
                {issue.severity !== 'error' && issue.available > 0 && (
                  <div className="issue-stats">
                    <span className="stat">Requested: {issue.requested} units</span>
                    <span className="stat-separator">•</span>
                    <span className="stat">Available: {issue.available} units</span>
                  </div>
                )}
              </div>
            </div>

            <button
              type="button"
              className="issue-remove-btn"
              onClick={() => handleRemoveItem(issue.productId)}
              title="Remove from cart"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="stock-warning-footer">
        <div className="warning-note">
          <Lightbulb {...iconProps} className="warning-note-icon" aria-hidden />
          <span><strong>Tip:</strong> Stock levels update in real-time. Refresh if you believe this is an error.</span>
        </div>
      </div>
    </div>
  );
};

export default StockWarning;
